import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2, Newspaper, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../lib/apiClient';

export const NewsManagementTab = () => {
  const [queue, setQueue] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState('tariff regulatory updates trade policy');
  const [region, setRegion] = useState('Global');
  const [count, setCount] = useState(5);

  const fetchQueue = useCallback(async () => {
    try {
      const d = await apiGet('/api/news/admin/queue');
      setQueue(d.articles || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const generateArticles = async () => {
    setGenerating(true);
    try {
      const d = await apiPost('/api/news/admin/fetch', { topic, region, count });
      toast.success(d.message);
      fetchQueue();
    } catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setGenerating(false); }
  };

  const reviewArticle = async (articleId, action) => {
    try {
      await apiPost(`/api/news/admin/queue/${articleId}/${action}`);
      toast.success(`Article ${action}d`);
      fetchQueue();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-news-tab">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4 flex items-center gap-2">
          <Newspaper className="w-5 h-5" /> Generate Articles
        </h3>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Topic</label>
            <input value={topic} onChange={e => setTopic(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Region</label>
            <select value={region} onChange={e => setRegion(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg">
              <option>Global</option><option>US</option><option>EU</option><option>UK</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Count</label>
            <input type="number" value={count} onChange={e => setCount(parseInt(e.target.value) || 1)} min={1} max={10} className="w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
        </div>
        <Button onClick={generateArticles} disabled={generating} className="bg-[#0B3D91] hover:bg-[#082A66]" data-testid="news-generate-btn">
          {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : 'Generate AI Articles'}
        </Button>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4">Pending Articles ({queue.length})</h3>
        {queue.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No articles pending review.</p>
        ) : (
          <div className="space-y-3">
            {queue.map(article => (
              <div key={article.article_id} className="p-4 bg-slate-50 rounded-xl" data-testid={`news-queue-${article.article_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{article.category}</span>
                      <span className="text-xs text-slate-400">{article.region}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        article.confidence === 'high' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>{article.confidence}</span>
                    </div>
                    <p className="font-medium text-sm text-slate-900">{article.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{article.summary}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 px-3 text-xs" onClick={() => reviewArticle(article.article_id, 'approve')}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Publish
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 px-3 text-xs text-red-600" onClick={() => reviewArticle(article.article_id, 'reject')}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
